import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IconMomoShield } from '../components/layout/NavIcons';
import { Spinner } from '../components/Spinner';
import { useAuth } from '../contexts/AuthContext';
import { API_BASE } from '../server/api';
import { supabase } from '../lib/supabase';
import { formatRelativeTime } from '../lib/timeFormat';

const MESSAGE_MAX = 5000;

type MomoSeverity = 'high' | 'medium' | 'low';

type RuleMatch = {
  rule: string;
  points: number;
  description: string;
};

type MomoAiClassification = {
  severity: MomoSeverity;
  confidence: number;
  reason: string;
};

type MomoAnalysisResult = {
  severity: MomoSeverity;
  score: number;
  title: string;
  reason: string;
  recommendation: string;
  ruleMatches: RuleMatch[];
  aiClassification: MomoAiClassification | null;
  analysisTimestamp: string;
};

type MomoEventRow = {
  id: string;
  risk_level: string;
  risk_reason: string | null;
  risk_score: number | null;
  source: string;
  created_at: string;
};

function startOfCurrentMonth(d = new Date()): Date {
  const x = new Date(d);
  x.setDate(1);
  x.setHours(0, 0, 0, 0);
  return x;
}

function staggerStyle(i: number): React.CSSProperties {
  return { animationDelay: `${80 + i * 60}ms` };
}

function severityStyle(level: string): {
  bar: string;
  badge: string;
  riskLabel: string;
} {
  const s = level.toLowerCase();
  if (s === 'high') {
    return {
      bar: 'bg-danger',
      badge: 'bg-danger/20 text-danger border-danger/30',
      riskLabel: 'HIGH RISK',
    };
  }
  if (s === 'medium') {
    return {
      bar: 'bg-warning',
      badge: 'bg-warning/20 text-warning border-warning/30',
      riskLabel: 'MEDIUM RISK',
    };
  }
  return {
    bar: 'bg-accent',
    badge: 'bg-accent/15 text-accent border-accent/30',
    riskLabel: 'LOW RISK',
  };
}

function severityMomo(level: MomoSeverity): {
  cardBorder: string;
  cardBg: string;
  ringColorClass: string;
  recommendationBox: string;
  confidenceBar: string;
} {
  if (level === 'high') {
    return {
      cardBorder: 'border-danger/40',
      cardBg: 'bg-danger/5',
      ringColorClass: 'text-danger',
      recommendationBox: 'border-danger/25 bg-danger/10',
      confidenceBar: 'bg-danger',
    };
  }
  if (level === 'medium') {
    return {
      cardBorder: 'border-warning/40',
      cardBg: 'bg-warning/5',
      ringColorClass: 'text-warning',
      recommendationBox: 'border-warning/25 bg-warning/10',
      confidenceBar: 'bg-warning',
    };
  }
  return {
    cardBorder: 'border-accent/40',
    cardBg: 'bg-accent/5',
    ringColorClass: 'text-accent',
    recommendationBox: 'border-accent/25 bg-accent/10',
    confidenceBar: 'bg-accent',
  };
}

const SCORE_RING_R = 32;
const SCORE_RING_C = 2 * Math.PI * SCORE_RING_R;

function MomoScoreRing({ score, severity }: { score: number; severity: MomoSeverity }) {
  const sev = severityMomo(severity);
  const [animatedScore, setAnimatedScore] = useState(0);

  useEffect(() => {
    const start = performance.now();
    const from = 0;
    const to = Math.min(100, Math.max(0, Math.round(score)));
    const duration = 800;
    let frameId = 0;

    function tick(now: number) {
      const t = Math.min(1, (now - start) / duration);
      const eased = 1 - (1 - t) ** 3;
      setAnimatedScore(Math.round(from + (to - from) * eased));
      if (t < 1) frameId = requestAnimationFrame(tick);
    }

    frameId = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(frameId);
  }, [score]);

  const offset = SCORE_RING_C * (1 - animatedScore / 100);

  return (
    <div className="relative h-20 w-20 shrink-0" aria-hidden>
      <svg className="-rotate-90" viewBox="0 0 80 80" width={80} height={80}>
        <circle
          cx="40"
          cy="40"
          r={SCORE_RING_R}
          fill="none"
          stroke="currentColor"
          strokeWidth={5}
          className="text-foreground/15"
        />
        <circle
          cx="40"
          cy="40"
          r={SCORE_RING_R}
          fill="none"
          stroke="currentColor"
          strokeWidth={5}
          strokeLinecap="round"
          strokeDasharray={SCORE_RING_C}
          className={sev.ringColorClass}
          style={{ strokeDashoffset: offset }}
        />
      </svg>
      <div className="pointer-events-none absolute inset-0 flex flex-col items-center justify-center text-center">
        <span className="text-lg font-bold leading-none tabular-nums text-foreground">{animatedScore}</span>
        <span className="mt-0.5 text-[10px] font-medium leading-none text-muted">/ 100</span>
      </div>
    </div>
  );
}

const LOADING_MESSAGES = [
  'Analyzing message…',
  'Scanning for fraud patterns…',
  'Checking against known scams…',
  'Running AI classification…',
] as const;

const EXAMPLE_PROMPTS: { title: string; body: string }[] = [
  {
    title: 'Example: Fake reversal',
    body: 'MTN is reversing GHS 500. Send your PIN to confirm.',
  },
  {
    title: 'Example: Boss scam',
    body: 'This is urgent. Send GHS 2000 to this number now. — Management',
  },
  {
    title: 'Example: Prize fraud',
    body: 'Congratulations! You won GHS 10,000. Send GHS 50 to claim.',
  },
];

function isMomoSeverity(x: unknown): x is MomoSeverity {
  return x === 'high' || x === 'medium' || x === 'low';
}

function isRuleMatch(x: unknown): x is RuleMatch {
  if (!x || typeof x !== 'object') return false;
  const o = x as Record<string, unknown>;
  return typeof o.rule === 'string' && typeof o.points === 'number' && typeof o.description === 'string';
}

function parseAnalysisResult(raw: unknown): MomoAnalysisResult | null {
  if (!raw || typeof raw !== 'object') return null;
  const o = raw as Record<string, unknown>;
  if (!isMomoSeverity(o.severity) || typeof o.score !== 'number') return null;
  if (typeof o.title !== 'string' || typeof o.reason !== 'string' || typeof o.recommendation !== 'string') {
    return null;
  }
  if (!Array.isArray(o.ruleMatches) || !o.ruleMatches.every(isRuleMatch)) return null;
  if (typeof o.analysisTimestamp !== 'string') return null;

  let ai: MomoAiClassification | null = null;
  if (o.aiClassification != null) {
    const a = o.aiClassification;
    if (typeof a === 'object' && a !== null) {
      const ac = a as Record<string, unknown>;
      if (
        isMomoSeverity(ac.severity) &&
        typeof ac.confidence === 'number' &&
        typeof ac.reason === 'string'
      ) {
        ai = {
          severity: ac.severity,
          confidence: ac.confidence,
          reason: ac.reason,
        };
      }
    }
  }

  return {
    severity: o.severity,
    score: o.score,
    title: o.title,
    reason: o.reason,
    recommendation: o.recommendation,
    ruleMatches: o.ruleMatches,
    aiClassification: ai,
    analysisTimestamp: o.analysisTimestamp,
  };
}

function sourceLabel(source: string): string {
  const s = source.toLowerCase();
  if (s === 'whatsapp') return 'WhatsApp';
  return 'API';
}

export default function MomoPage() {
  const { currentUser, loading: authLoading } = useAuth();
  const userId = currentUser?.id ?? null;
  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const [message, setMessage] = useState('');
  const [checking, setChecking] = useState(false);
  const [checkError, setCheckError] = useState<string | null>(null);
  const [result, setResult] = useState<MomoAnalysisResult | null>(null);
  const [loadingMsgIndex, setLoadingMsgIndex] = useState(0);
  const [loadingMsgOpaque, setLoadingMsgOpaque] = useState(true);

  const [totalChecked, setTotalChecked] = useState<number | null>(null);
  const [threatsHigh, setThreatsHigh] = useState<number | null>(null);
  const [thisMonthCount, setThisMonthCount] = useState<number | null>(null);
  const [events, setEvents] = useState<MomoEventRow[]>([]);
  const [statsLoading, setStatsLoading] = useState(true);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [eventsError, setEventsError] = useState<string | null>(null);

  const monthStartIso = useMemo(() => startOfCurrentMonth().toISOString(), []);

  const loadMomoData = useCallback(async () => {
    if (!userId) {
      setTotalChecked(null);
      setThreatsHigh(null);
      setThisMonthCount(null);
      setEvents([]);
      setStatsLoading(false);
      return;
    }

    setStatsLoading(true);
    setStatsError(null);
    setEventsError(null);

    const [totalRes, highRes, monthRes, eventsRes] = await Promise.all([
      supabase.from('momo_events').select('*', { count: 'exact', head: true }).eq('user_id', userId),
      supabase
        .from('momo_events')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .eq('risk_level', 'high'),
      supabase
        .from('momo_events')
        .select('*', { count: 'exact', head: true })
        .eq('user_id', userId)
        .gte('created_at', monthStartIso),
      supabase
        .from('momo_events')
        .select('id,risk_level,risk_reason,risk_score,source,created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false })
        .limit(10),
    ]);

    const statsFailed = Boolean(totalRes.error || highRes.error || monthRes.error);
    const eventsFailed = Boolean(eventsRes.error);

    if (statsFailed) {
      setTotalChecked(null);
      setThreatsHigh(null);
      setThisMonthCount(null);
    } else {
      setTotalChecked(totalRes.count ?? 0);
      setThreatsHigh(highRes.count ?? 0);
      setThisMonthCount(monthRes.count ?? 0);
    }

    if (eventsFailed || !eventsRes.data) {
      setEvents([]);
    } else {
      setEvents(eventsRes.data as MomoEventRow[]);
    }

    if (statsFailed) {
      setStatsError('Could not load MoMo statistics.');
    }
    if (eventsFailed) {
      setEventsError('Could not load recent MoMo events.');
    }

    setStatsLoading(false);
  }, [userId, monthStartIso]);

  useEffect(() => {
    void loadMomoData();
  }, [loadMomoData]);

  useEffect(() => {
    if (!checking) {
      setLoadingMsgIndex(0);
      setLoadingMsgOpaque(true);
      return;
    }
    const id = window.setInterval(() => {
      setLoadingMsgOpaque(false);
      window.setTimeout(() => {
        setLoadingMsgIndex((i) => (i + 1) % LOADING_MESSAGES.length);
        setLoadingMsgOpaque(true);
      }, 220);
    }, 1500);
    return () => window.clearInterval(id);
  }, [checking]);

  const handleCheckAnother = () => {
    setResult(null);
    setMessage('');
    setCheckError(null);
    window.scrollTo({ top: 0, behavior: 'smooth' });
    window.requestAnimationFrame(() => {
      textareaRef.current?.focus();
    });
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (checking) return;
    setCheckError(null);
    setResult(null);

    const trimmed = message.trim();
    if (!trimmed) {
      setCheckError('Enter a message to analyze.');
      return;
    }

    setChecking(true);
    try {
      const {
        data: { session },
        error: sessionError,
      } = await supabase.auth.getSession();
      if (sessionError || !session?.access_token) {
        setCheckError('Your session expired. Please sign in again.');
        setChecking(false);
        return;
      }

      const res = await fetch(`${API_BASE}/api/momo/check`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${session.access_token}`,
        },
        body: JSON.stringify({ message: trimmed }),
      });

      let body: unknown;
      try {
        body = await res.json();
      } catch {
        setCheckError('Invalid response from server.');
        setChecking(false);
        return;
      }

      if (!res.ok) {
        const errMsg =
          typeof body === 'object' &&
          body !== null &&
          'error' in body &&
          typeof (body as { error: unknown }).error === 'string'
            ? (body as { error: string }).error
            : `Request failed (${res.status}).`;
        setCheckError(errMsg);
        setChecking(false);
        return;
      }

      if (
        typeof body !== 'object' ||
        body === null ||
        !('ok' in body) ||
        (body as { ok: unknown }).ok !== true ||
        !('result' in body)
      ) {
        setCheckError('Analysis failed. Try again.');
        setChecking(false);
        return;
      }

      const parsed = parseAnalysisResult((body as { result: unknown }).result);
      if (!parsed) {
        setCheckError('Unexpected analysis format.');
        setChecking(false);
        return;
      }

      setResult(parsed);
      window.setTimeout(() => {
        void loadMomoData();
      }, 500);
    } catch {
      setCheckError('Could not reach the server. Check your connection.');
    } finally {
      setChecking(false);
    }
  };

  if (authLoading) {
    return null;
  }

  if (!userId) {
    return (
      <div className="mx-auto max-w-4xl py-12 text-center text-muted">
        <p>Sign in to use MoMo Protection.</p>
      </div>
    );
  }

  const charCount = message.length;

  const showExampleCards =
    result === null && message === '' && events.length === 0 && !statsLoading && !checking;

  return (
    <div className="mx-auto max-w-4xl space-y-8">
      <section
        className="opacity-0 animate-fade-in-up rounded-2xl border border-border bg-surface/60 p-5 sm:p-6"
        style={staggerStyle(0)}
      >
        <h2 className="text-2xl font-semibold tracking-tight text-foreground sm:text-3xl">MoMo Fraud Checker</h2>
        <p className="mt-2 max-w-2xl text-sm leading-relaxed text-muted sm:text-base">
          Forward any suspicious message and we&apos;ll analyze it instantly
        </p>

        {result === null ? (
          <>
            <form onSubmit={(ev) => void handleSubmit(ev)} className="mt-6 space-y-4">
              <div>
                <label htmlFor="momo-message" className="sr-only">
                  Suspicious message
                </label>
                <textarea
                  ref={textareaRef}
                  id="momo-message"
                  value={message}
                  maxLength={MESSAGE_MAX}
                  onChange={(ev) => setMessage(ev.target.value)}
                  placeholder="Paste the suspicious message here..."
                  rows={6}
                  className={`w-full resize-y rounded-xl border border-border bg-navy/60 px-4 py-3 text-sm text-foreground placeholder:text-muted focus:border-accent/50 focus:outline-none focus:ring-2 focus:ring-accent/25 ${
                    checking ? 'pointer-events-none opacity-50' : ''
                  }`}
                />
                <div className="mt-1 flex justify-end text-xs text-muted">
                  <span>
                    {charCount}/{MESSAGE_MAX}
                  </span>
                </div>
              </div>

              {checkError ? (
                <div
                  className="rounded-xl border border-danger/30 bg-danger/10 px-4 py-3 text-sm text-danger"
                  role="alert"
                >
                  {checkError}
                </div>
              ) : null}

              <button
                type="submit"
                disabled={checking || !message.trim()}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-accent px-5 py-2.5 text-sm font-semibold text-cta-text transition-opacity hover:opacity-90 disabled:pointer-events-none disabled:opacity-50"
              >
                {checking ? (
                  <>
                    <Spinner className="h-5 w-5 text-cta-text" />
                    Analyzing…
                  </>
                ) : (
                  'Check Message'
                )}
              </button>
            </form>

            {checking ? (
              <div
                className="mt-6 rounded-2xl border border-border bg-surface/60 p-6 opacity-0 animate-fade-in-up"
                style={staggerStyle(1)}
                aria-busy="true"
                aria-label="Analyzing message"
              >
                <div className="flex flex-col items-center gap-4 text-center">
                  <Spinner className="h-10 w-10 text-accent" />
                  <p
                    className={`min-h-[1.25rem] text-sm text-muted transition-opacity duration-200 ease-out ${
                      loadingMsgOpaque ? 'opacity-100' : 'opacity-0'
                    }`}
                  >
                    {LOADING_MESSAGES[loadingMsgIndex]}
                  </p>
                </div>
              </div>
            ) : null}

            {showExampleCards ? (
              <div className="mt-6 space-y-3">
                <p className="text-xs font-semibold uppercase tracking-wide text-muted">Try an example</p>
                <div className="-mx-1 flex gap-3 overflow-x-auto pb-1 pt-0.5 sm:mx-0 sm:flex-wrap sm:overflow-visible">
                  {EXAMPLE_PROMPTS.map((ex) => (
                    <button
                      key={ex.title}
                      type="button"
                      onClick={() => setMessage(ex.body)}
                      className="min-w-[220px] shrink-0 cursor-pointer rounded-xl border border-border bg-navy/40 p-3 text-left transition hover:border-accent/30 hover:bg-accent/5 sm:min-w-0 sm:flex-1 sm:basis-[calc(33.333%-0.5rem)]"
                    >
                      <span className="block text-xs font-medium text-foreground">{ex.title}</span>
                      <span className="mt-2 block text-sm italic text-muted">&ldquo;{ex.body}&rdquo;</span>
                    </button>
                  ))}
                </div>
              </div>
            ) : null}
          </>
        ) : null}
      </section>

      {result ? (
        <>
          {(() => {
            const sx = severityMomo(result.severity);
            const sev = severityStyle(result.severity);
            const confPct = result.aiClassification
              ? Math.round(Math.min(1, Math.max(0, result.aiClassification.confidence)) * 100)
              : 0;
            return (
              <section
                className={`opacity-0 animate-fade-in-up overflow-hidden rounded-2xl border bg-surface/60 ${sx.cardBorder} ${sx.cardBg}`}
                style={staggerStyle(1)}
              >
                <div className="flex">
                  <div className={`w-1 shrink-0 ${sev.bar}`} aria-hidden />
                  <div className="min-w-0 flex-1 p-5 sm:p-6">
                    <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${sev.badge}`}>
                            {sev.riskLabel}
                          </span>
                        </div>
                        <h3 className="mt-2 text-base font-semibold leading-snug text-foreground sm:text-lg">
                          {result.title}
                        </h3>
                      </div>
                      <MomoScoreRing score={result.score} severity={result.severity} />
                    </div>
                    <p className="mt-3 text-sm leading-relaxed text-muted">{result.reason}</p>
                    <div className={`mt-4 rounded-xl border p-4 ${sx.recommendationBox}`}>
                      <p className="text-xs font-semibold uppercase tracking-wide text-muted">Recommendation</p>
                      <p className="mt-2 text-sm leading-relaxed text-foreground">{result.recommendation}</p>
                    </div>
                    {result.ruleMatches.length > 0 ? (
                      <div className="mt-4">
                        <p className="text-xs font-semibold uppercase tracking-wide text-muted">Rule matches</p>
                        <div className="mt-2 flex flex-wrap gap-2">
                          {result.ruleMatches.map((m) => {
                            const heavy = m.points >= 30;
                            return (
                              <span
                                key={`${m.rule}-${m.points}-${m.description.slice(0, 24)}`}
                                className={`rounded-full border px-2.5 py-1 text-xs ${
                                  heavy
                                    ? 'border-danger/30 bg-danger/5 font-semibold text-danger'
                                    : 'border-border bg-foreground/5 font-medium text-foreground'
                                }`}
                                title={m.description}
                              >
                                {m.rule} (+{m.points})
                              </span>
                            );
                          })}
                        </div>
                      </div>
                    ) : null}
                    {result.aiClassification ? (
                      <div className="mt-4 rounded-xl border border-border bg-foreground/5 p-4">
                        <p className="text-xs font-medium uppercase tracking-wide text-muted">AI assessment</p>
                        <p className="mt-2 text-sm text-foreground">
                          <span className="text-muted">AI Assessment: </span>
                          {result.aiClassification.reason}
                        </p>
                        <p className="mt-2 text-xs text-muted">Confidence: {confPct}%</p>
                        <div className="mt-2 h-1 w-full overflow-hidden rounded-full bg-foreground/10">
                          <div
                            className={`h-full rounded-full ${sx.confidenceBar} transition-all duration-500`}
                            style={{ width: `${confPct}%` }}
                          />
                        </div>
                      </div>
                    ) : null}
                  </div>
                </div>
              </section>
            );
          })()}

          <div
            className="flex flex-col gap-3 opacity-0 animate-fade-in-up sm:flex-row sm:items-center sm:justify-between"
            style={staggerStyle(2)}
          >
            <button
              type="button"
              onClick={handleCheckAnother}
              className="inline-flex w-full items-center justify-center rounded-lg border border-accent/40 bg-accent/10 px-5 py-2.5 text-sm font-semibold text-accent transition-colors hover:bg-accent/20 sm:w-auto"
            >
              Check Another Message
            </button>
            <Link
              to="/alerts"
              className="text-center text-sm font-semibold text-accent underline underline-offset-2 sm:text-right"
            >
              View in Alerts
            </Link>
          </div>
        </>
      ) : null}

      <section className="opacity-0 animate-fade-in-up space-y-4" style={staggerStyle(3)}>
        <h3 className="text-lg font-semibold text-foreground">Your MoMo activity</h3>
        {statsLoading ? (
          <div className="flex min-h-[120px] flex-col items-center justify-center gap-2 rounded-2xl border border-border bg-surface/40 py-8 text-muted">
            <Spinner className="h-8 w-8 text-accent" />
            <p className="text-sm">Loading stats…</p>
          </div>
        ) : statsError ? (
          <div className="rounded-2xl border border-border bg-surface/60 p-6 text-center">
            <p className="text-sm text-foreground">{statsError}</p>
            <button
              type="button"
              onClick={() => void loadMomoData()}
              className="mt-4 rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-semibold text-accent hover:bg-accent/20"
            >
              Try again
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
            <div className="rounded-xl border border-border bg-surface/60 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-muted">Total checked</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">{totalChecked ?? 0}</p>
              <p className="mt-0.5 text-xs text-muted">Messages analyzed</p>
            </div>
            <div className="rounded-xl border border-border bg-surface/60 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-danger">Threats detected</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-danger">{threatsHigh ?? 0}</p>
              <p className="mt-0.5 text-xs text-muted">High risk results</p>
            </div>
            <div className="rounded-xl border border-border bg-surface/60 p-4">
              <p className="text-xs font-medium uppercase tracking-wide text-accent">This month</p>
              <p className="mt-1 text-2xl font-bold tabular-nums text-foreground">{thisMonthCount ?? 0}</p>
              <p className="mt-0.5 text-xs text-muted">Checks this month</p>
            </div>
          </div>
        )}
      </section>

      <section className="space-y-4">
        <h3 className="text-lg font-semibold text-foreground opacity-0 animate-fade-in-up" style={staggerStyle(4)}>
          Recent MoMo events
        </h3>
        {statsLoading && events.length === 0 && !eventsError ? (
          <div className="flex min-h-[160px] flex-col items-center justify-center gap-2 text-muted">
            <Spinner className="h-8 w-8 text-accent" />
            <p className="text-sm">Loading events…</p>
          </div>
        ) : eventsError ? (
          <div
            className="opacity-0 animate-fade-in-up rounded-2xl border border-border bg-surface/60 p-6 text-center"
            style={staggerStyle(5)}
          >
            <p className="text-sm text-foreground">{eventsError}</p>
            <button
              type="button"
              onClick={() => void loadMomoData()}
              className="mt-4 rounded-lg border border-accent/40 bg-accent/10 px-4 py-2 text-sm font-semibold text-accent hover:bg-accent/20"
            >
              Try again
            </button>
          </div>
        ) : events.length === 0 ? (
          <div
            className="opacity-0 animate-fade-in-up flex flex-col items-center justify-center rounded-2xl border border-border bg-surface/40 px-6 py-16 text-center"
            style={staggerStyle(5)}
          >
            <IconMomoShield className="h-20 w-20 text-accent sm:h-24 sm:w-24" />
            <h3 className="mt-6 text-lg font-semibold text-foreground sm:text-xl">No messages checked yet</h3>
            <p className="mt-2 max-w-md text-sm text-muted sm:text-base">
              Paste a suspicious message above to get started.
            </p>
          </div>
        ) : (
          <ul className="space-y-4">
            {events.map((ev, i) => {
              const sev = severityStyle(ev.risk_level);
              return (
                <li
                  key={ev.id}
                  className="opacity-0 animate-fade-in-up overflow-hidden rounded-2xl border border-border bg-surface/55"
                  style={staggerStyle(Math.min(6 + i, 14))}
                >
                  <div className="flex">
                    <div className={`w-1 shrink-0 ${sev.bar}`} aria-hidden />
                    <div className="min-w-0 flex-1 p-4 sm:p-5">
                      <div className="flex flex-wrap items-center gap-2 gap-y-2">
                        <span className={`rounded-full border px-2.5 py-0.5 text-xs font-semibold ${sev.badge}`}>
                          {sev.riskLabel}
                        </span>
                        {ev.risk_score != null ? (
                          <span className="text-xs font-medium tabular-nums text-muted">{ev.risk_score}/100</span>
                        ) : null}
                        <span className="rounded-full border border-border bg-foreground/5 px-2.5 py-0.5 text-xs font-medium text-muted">
                          {sourceLabel(ev.source)}
                        </span>
                        <span className="ml-auto text-xs text-muted sm:text-sm">
                          {formatRelativeTime(ev.created_at)}
                        </span>
                      </div>
                      <p className="mt-3 text-base font-medium leading-snug text-foreground">
                        {ev.risk_reason?.trim() || 'No reason recorded'}
                      </p>
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </section>
    </div>
  );
}
